import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Calendar, Plane, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import PageLayout from '../components/PageLayout';
import NotApplicable from '../components/NotApplicable';

const MyTripsRedirect = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noTrips, setNoTrips] = useState(false);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const res = await api.get('/my-trips');
        const data = res.data || [];
        if (data.length === 0) {
          setNoTrips(true);
        } else {
          // Sort soonest first
          data.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
          setTrips(data);
        }
      } catch {
        setNoTrips(true);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, []);

  const getDaysUntil = (dateStr) => {
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (noTrips) {
    return (
      <PageLayout title="My Trips">
        <NotApplicable
          title="No Trips Assigned"
          message="You haven't been assigned to any trips yet. Once a trip coordinator adds you to a trip, it will appear here."
          linkTo="/dashboard"
          linkLabel="Back to Dashboard"
        />
      </PageLayout>
    );
  }

  const today = new Date();

  return (
    <PageLayout title="My Trips">
      <div className="space-y-4 max-w-2xl">
        {trips.map((trip) => {
          const startDate = new Date(trip.start_date);
          const endDate = new Date(trip.end_date);
          const isUpcoming = startDate > today;
          const isActive = startDate <= today && endDate >= today;
          const isPast = endDate < today;
          const days = getDaysUntil(trip.start_date);

          return (
            <Link key={trip.trip_id} to={`/trips/${trip.trip_id}`} className="block" data-testid={`my-trip-${trip.trip_id}`}>
              <Card className={`transition-all hover:shadow-md ${isActive ? 'border-green-300 bg-green-50/30' : isPast ? 'opacity-60' : 'hover:border-primary/30'}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading font-bold text-lg text-slate-800 truncate">{trip.name}</h3>
                        {isActive && <Badge className="bg-green-100 text-green-700 border-0 text-xs">Active Now</Badge>}
                        {isPast && <Badge className="bg-slate-100 text-slate-500 border-0 text-xs">Completed</Badge>}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          {trip.start_date} – {trip.end_date}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Plane className="w-4 h-4" />
                          {trip.location}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {isUpcoming && (
                        <div className="text-right">
                          <p className="text-2xl font-heading font-bold text-primary">{days}</p>
                          <p className="text-xs text-slate-400">days</p>
                        </div>
                      )}
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </PageLayout>
  );
};

export default MyTripsRedirect;
